/* eslint-disable react-refresh/only-export-components */
import PropTypes from "prop-types";
import { createContext, useContext, useEffect, useState } from "react";
import { ConfigProvider, theme } from 'antd';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [themeMode, setThemeMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) return savedTheme;
    
    // Check system preference
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.setAttribute('data-theme', themeMode);
    localStorage.setItem('theme', themeMode);
  }, [themeMode]);

  const toggleTheme = (newTheme) => {
    if (newTheme === 'default') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      setThemeMode(systemTheme);
    } else {
      setThemeMode(newTheme);
    }
  };

  // Ant Design theme configuration
  const antdTheme = {
    algorithm: themeMode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: themeMode === 'dark' ? {
      colorPrimary: 'var(--veegil-color-primary)',
      colorSuccess: 'var(--veegil-color-green)',
      colorWarning: 'var(--veegil-color-warning)',
      colorError: 'var(--veegil-color-danger)',
      colorInfo: 'var(--veegil-color-primary)',
    } : undefined,
    components: themeMode === 'dark' ? {
      Menu: {
        colorItemBg: 'var(--background-color)',
        colorItemText: 'var(--text-color)',
        colorItemTextSelected: 'var(--veegil-color-primary)',
        colorItemBgSelected: 'var(--veegil-color-secondary)',
      },
      Table: {
        colorTextHeading: '#000000',
        headerBg: '#f0f0f0',
        headerColor: '#000000',
        headerSplitColor: '#d9d9d9',
        colorBgContainer: 'var(--header-bg)',
        colorBorderSecondary: 'var(--header-bg)',
        colorFillAlter: 'var(--header-bg)',
        rowHoverBg: 'var(--sidebar-bg)',
      },
      Button: {
        colorPrimary: '#0F75BC',
        colorPrimaryHover: '#094167',
        colorTextLightSolid: '#FFFFFF',
        defaultBg: '#094167',
        defaultColor: '#ACCEF0',
      },
      Modal: {
        contentBg: '#0F161F',
        headerBg: '#0F161F',
        titleColor: '#FFFFFF',
        contentColor: '#FFFFFF',
        footerBg: '#162333',
      }
    } : undefined
  };

  return (
    <ConfigProvider theme={antdTheme}>
      <ThemeContext.Provider value={{ theme: themeMode, toggleTheme }}>
        {children}
      </ThemeContext.Provider>
    </ConfigProvider>
  );
};

ThemeProvider.propTypes = {
  children: PropTypes.node,
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};